import { db, storage } from '../config/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { Post } from '../types/post';
import axios from 'axios';
import { configService } from './configService';

const COLLECTION_NAME = 'posts';

// Helper function to check if URL is a Firebase Storage URL
const isFirebaseStorageUrl = (url: string): boolean => {
    try {
        return url.includes('firebasestorage.googleapis.com') || 
               url.includes('storage.googleapis.com') ||
               url.startsWith('gs://');
    } catch {
        return false;
    }
};

export const postService = {
  async addPost(post: Post, imageFiles: File[]) {
    try {
      const imageUrls = await Promise.all(
        imageFiles.map(async (file) => {
          const storageRef = ref(storage, `posts/${Date.now()}_${file.name}`);
          const uploadResult = await uploadBytes(storageRef, file);
          return getDownloadURL(uploadResult.ref);
        })
      );

      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...post,
        imageUrls: imageUrls,
        status: 'draft',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      return { id: docRef.id, ...post, imageUrls };
    } catch (error) {
      throw new Error('Could not add post: ' + error);
    }
  },

  async updatePost(id: string, post: Partial<Post>, newImageFiles: File[]) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const currentDoc = await getDoc(docRef);

      if (!currentDoc.exists()) {
        throw new Error('Post not found');
      }

      let updateData = {
        ...post,
        updatedAt: new Date()
      };

      if (newImageFiles.length > 0) {
        // Delete old images if they exist
        const currentImageUrls = currentDoc.data().imageUrls || [];
        
        // Xóa từng ảnh cũ với kiểm tra URL hợp lệ
        let deletedOldCount = 0;
        let skippedOldCount = 0;
        
        for (const url of currentImageUrls) {
          if (isFirebaseStorageUrl(url)) {
            try {
              const oldImageRef = ref(storage, url);
              await deleteObject(oldImageRef);
              deletedOldCount++;
              console.log(`Successfully deleted old post image: ${url}`);
            } catch (error) {
              console.error(`Error deleting old post image ${url}:`, error);
            }
          } else {
            skippedOldCount++;
            console.log(`Skipped non-Firebase Storage URL: ${url}`);
          }
        }

        if (currentImageUrls.length > 0) {
          console.log(`Update cleanup for post ID ${id}: ${deletedOldCount} old images deleted, ${skippedOldCount} non-Firebase URLs skipped`);
        }

        // Upload new images
        const newImageUrls = await Promise.all(
          newImageFiles.map(async (file) => {
            const storageRef = ref(storage, `posts/${Date.now()}_${file.name}`);
            const uploadResult = await uploadBytes(storageRef, file);
            return getDownloadURL(uploadResult.ref);
          })
        );
        updateData.imageUrls = newImageUrls;
      }

      await updateDoc(docRef, updateData);
      return { id, ...updateData };
    } catch (error) {
      throw new Error('Could not update post: ' + error);
    }
  },

  async deletePost(id: string) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const postData = docSnap.data();
        
        // Delete from Zalo if it has zaloId
        if (postData.zaloId) {
          const newConfigZalo = await configService.getConfig();
          const response = await axios.post(
            'https://openapi.zalo.me/v2.0/article/remove',
            {
              id: postData.zaloId
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'access_token': newConfigZalo?.access_token_zalo
              }
            }
          );

          if (response.data.error !== 0) {
            throw new Error('Failed to delete Zalo post: ' + response.data.message);
          }
        }

        // Delete images from storage only if they are Firebase URLs
        const imageUrls = postData.imageUrls || [];
        
        // Xóa tất cả ảnh từ Firebase Storage
        let deletedCount = 0;
        let skippedCount = 0;
        
        for (const url of imageUrls) {
          // Chỉ xóa nếu là Firebase Storage URL
          if (isFirebaseStorageUrl(url)) {
            try {
              const imageRef = ref(storage, url);
              await deleteObject(imageRef);
              deletedCount++;
              console.log(`Successfully deleted post image: ${url}`);
            } catch (error) {
              console.error(`Error deleting post image ${url}:`, error);
              // Tiếp tục xử lý dù có lỗi khi xóa ảnh
            }
          } else {
            skippedCount++;
            console.log(`Skipped non-Firebase Storage URL: ${url}`);
          }
        }

        // Ghi log để theo dõi số lượng ảnh đã xóa
        console.log(`Storage cleanup for post ID ${id}: ${deletedCount} images deleted, ${skippedCount} non-Firebase URLs skipped`);

        // Delete from Firebase
        await deleteDoc(docRef);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      throw new Error('Could not delete post: ' + error);
    }
  },

  async getAllPosts() {
    const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Post[];
  },

  async saveZaloPost(zaloPost: any) {
    try {
      const zaloId = zaloPost.id.toString();
      console.log('Checking for existing post with Zalo ID:', zaloId);

      console.log('zaloPost: ', zaloPost);

      // Convert timestamps from milliseconds to Date objects
      const formatDate = (timestamp: number) => {
        // Convert to milliseconds if needed
        const ms = timestamp > 9999999999 ? timestamp : timestamp * 1000;
        const date = new Date(ms);
        return date;
      };

      // Convert Zalo post data to our Post format
      const postData: Partial<Post> = {
        zaloId: zaloId,
        title: zaloPost.title || '',
        description: zaloPost.description || '',
        content: zaloPost.content || '',
        status: zaloPost.status || 'draft',
        imageUrls: zaloPost.thumb ? [zaloPost.thumb] : [],
        createdAt: formatDate(zaloPost.create_date),
        updatedAt: formatDate(zaloPost.update_date),
        
        // Zalo specific fields
        total_view: zaloPost.total_view,
        total_share: zaloPost.total_share,
        thumb: zaloPost.thumb,
        link_view: zaloPost.link_view,
        type: zaloPost.type || 'normal',
        source: 'zalo'
      };

      // Check if post exists using zaloId
      const querySnapshot = await getDocs(
        query(collection(db, COLLECTION_NAME), where('zaloId', '==', zaloId))
      );

      console.log('Found existing posts:', querySnapshot.size);

      if (!querySnapshot.empty) {
        // Update existing post
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          ...postData,
          updatedAt: new Date()
        });
        console.log('Updated existing post with Zalo ID:', zaloId);
        return { id: docRef.id, ...postData };
      } else {
        // Add new post
        const docRef = await addDoc(collection(db, COLLECTION_NAME), postData);
        console.log('Added new post with Zalo ID:', zaloId);
        return { id: docRef.id, ...postData };
      }
    } catch (error) {
      console.error('Error saving/updating Zalo post:', error);
      throw error;
    }
  },

  async syncZaloPosts() {
    try {
      const newConfigZalo = await configService.getConfig();
      const allPosts: any[] = [];
      const processedIds = new Set<string>(); // Track processed IDs

      const fetchPostsRecursively = async (offset: number) => {
        const response = await axios.get(
          `https://openapi.zalo.me/v2.0/article/getslice?offset=${offset}&limit=10&type=normal`,
          {
            headers: {
              'access_token': newConfigZalo?.access_token_zalo
            }
          }
        );

        console.log('response: ', response);

        if (!response.data?.data?.medias) {
          console.log('Full response:', response);
          throw new Error('Invalid response structure');
        }

        const medias = response.data.data.medias;
        const total = response.data.data.total || 0;
        console.log(`Fetched ${medias.length} posts. Total available: ${total}. Current offset: ${offset}`);

        // Process each post
        for (const post of medias) {
          const zaloId = post.id.toString();
          if (!processedIds.has(zaloId)) {
            processedIds.add(zaloId);
            const savedPost = await this.saveZaloPost(post);
            if (savedPost) {
              allPosts.push(savedPost);
            }
          } else {
            console.log('Skipping duplicate post with Zalo ID:', zaloId);
          }
        }

        // If we received posts and haven't reached the total, continue fetching
        if (medias.length === 10 && allPosts.length < total) {
          await fetchPostsRecursively(offset + 10);
        }
      };

      await fetchPostsRecursively(0);
      console.log('Total unique posts fetched and saved:', allPosts.length);
      return allPosts;
    } catch (error) {
      console.error('Error syncing Zalo posts:', error);
      throw error;
    }
  },

  async createZaloPost(postData: any, imageFile?: File) {
    try {
      let imageUrl = '';
      
      // Upload image to Firebase Storage if exists
      if (imageFile) {
        const storageRef = ref(storage, `posts/${Date.now()}_${imageFile.name}`);
        const uploadResult = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(uploadResult.ref);
      }

      // Then create on Zalo
      const newConfigZalo = await configService.getConfig();
      
      // Prepare post data for Zalo API
      const zaloPostData = {
        type: "normal",
        title: postData.title,
        author: postData.author || "",
        cover: imageUrl ? {
          cover_type: "photo",
          photo_url: imageUrl,
          status: "show"
        } : undefined,
        description: postData.description,
        body: [
          {
            type: "text",
            content: postData.content
          }
        ],
        status: "show",
        comment: "show"
      };

      // Create post on Zalo
      const createResponse = await axios.post(
        'https://openapi.zalo.me/v2.0/article/create',
        zaloPostData,
        {
          headers: {
            'Content-Type': 'application/json',
            'access_token': newConfigZalo?.access_token_zalo
          }
        }
      );

      if (createResponse.data.error === 0) {
        // console.log('createResponse: ', createResponse.data.data);
        
        // const token = createResponse.data.data.token;

        // console.log('token: ', token);
        
        // // Wait for 2 seconds
        // await new Promise(resolve => setTimeout(resolve, 1000));

        // // Verify post with token
        // const verifyResponse = await axios.post(
        //   'https://openapi.zalo.me/v2.0/article/verify',
        //   {
        //     token: token
        //   },
        //   {
        //     headers: {
        //       'Content-Type': 'application/json',
        //       'access_token': newConfigZalo?.access_token_zalo
        //     }
        //   }
        // );

        // if (verifyResponse.data.error === 0) {
        //   const zaloId = verifyResponse.data.data.id;

        //   console.log('zaloId: ', zaloId);

        //   // Get post details
        //   const detailResponse = await axios.get(
        //     'https://openapi.zalo.me/v2.0/article/getdetail',
        //     {
        //       params: { id: zaloId },
        //       headers: {
        //         'access_token': newConfigZalo?.access_token_zalo
        //       }
        //     }
        //   );

        //   if (detailResponse.data.error === 0) {
        //     console.log('detailResponse: ', detailResponse.data.data);
            
        //     // Save post to Firebase
        //     await this.saveZaloPost(detailResponse.data.data);
        //     return detailResponse.data.data;
        //   }
        // }
        
        // throw new Error('Failed to verify or get details of Zalo post');
      } else {
        // If Zalo creation fails, delete the uploaded image
        // if (imageUrl) {
        //   const imageRef = ref(storage, imageUrl);
        //   try {
        //     await deleteObject(imageRef);
        //   } catch (error) {
        //     console.error('Error deleting image:', error);
        //   }
        // }
        throw new Error(createResponse.data.message || 'Failed to create Zalo post');
      }
    } catch (error) {
      console.error('Error creating Zalo post:', error);
      throw error;
    }
  },

  async updateZaloPost(id: string, postData: any, imageFile?: File) {
    try {
      let imageUrl = '';
      
      // Upload image to Firebase Storage if exists
      if (imageFile) {
        const storageRef = ref(storage, `posts/${Date.now()}_${imageFile.name}`);
        const uploadResult = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(uploadResult.ref);
      }

      // Update on Zalo
      const newConfigZalo = await configService.getConfig();
      
      // Prepare post data for Zalo API
      const zaloPostData = {
        id: id,
        type: "normal",
        title: postData.title,
        author: postData.author || "",
        cover: imageUrl ? {
          cover_type: "photo",
          photo_url: imageUrl,
          status: "show"
        } : undefined,
        description: postData.description,
        body: [
          {
            type: "text",
            content: postData.content
          }
        ],
        status: "show",
        comment: "show"
      };

      // Update post on Zalo
      const response = await axios.post(
        'https://openapi.zalo.me/v2.0/article/update',
        zaloPostData,
        {
          headers: {
            'Content-Type': 'application/json',
            'access_token': newConfigZalo?.access_token_zalo
          }
        }
      );

      if (response.data.error === 0) {
        // Get updated post details from Zalo
        const detailResponse = await axios.get(
          'https://openapi.zalo.me/v2.0/article/getdetail',
          {
            params: { id },
            headers: {
              'access_token': newConfigZalo?.access_token_zalo
            }
          }
        );

        if (detailResponse.data.error === 0) {
          // Find Firebase document ID by zaloId
          const querySnapshot = await getDocs(
            query(collection(db, COLLECTION_NAME), where('zaloId', '==', id))
          );

          if (!querySnapshot.empty) {
            const docRef = querySnapshot.docs[0].ref;
            const currentDoc = await getDoc(docRef);
            
            if (currentDoc.exists()) {
              const currentData = currentDoc.data();
              // Prepare update data
              const updateData = {
                title: postData.title,
                description: postData.description,
                content: postData.content,
                author: postData.author || "",
                updatedAt: new Date(),
                imageUrls: imageUrl ? [imageUrl] : (currentData.imageUrls || []),
                status: 'show',
                source: 'zalo',
                
                // Update Zalo specific fields
                ...(detailResponse.data.data.total_view !== undefined && {
                  total_view: detailResponse.data.data.total_view
                }),
                ...(detailResponse.data.data.total_share !== undefined && {
                  total_share: detailResponse.data.data.total_share
                }),
                ...(detailResponse.data.data.thumb && {
                  thumb: detailResponse.data.data.thumb
                }),
                ...(detailResponse.data.data.link_view && {
                  link_view: detailResponse.data.data.link_view
                })
              };

              // Update in Firebase
              await updateDoc(docRef, updateData);
            }
          }

          return detailResponse.data.data;
        }
      }
      
      throw new Error(response.data.message || 'Failed to update Zalo post');
    } catch (error) {
      console.error('Error updating Zalo post:', error);
      throw error;
    }
  }
}; 